import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
  Download, Trash2, FileText, Loader2, Calendar, Presentation, Eye,
  RefreshCw, Layers, ThumbsUp, ThumbsDown, MessageSquare, Share2, Users, Clock, Plus, Link2 as Link2Icon, RotateCcw
} from "lucide-react";
import { format } from "date-fns";
import ReferralCelebrationBanner from "@/components/ReferralCelebrationBanner";
import ShareLinkDialog from "@/components/ShareLinkDialog";

const StatusBadge = ({ status, detail }) => {
  if (status === "generating") return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 max-w-full"
      data-testid="status-generating"
      title={detail || "Generating..."}
    >
      <Loader2 className="w-3 h-3 animate-spin shrink-0" />
      <span className="truncate max-w-[280px]">{detail || "Generating..."}</span>
    </span>
  );
  // Failed jobs are always surfaced as "Queued" — the user can keep clicking
  // Retry indefinitely. Per product spec we no longer expose a terminal
  // "Failure" state in the UI. Color matches the in-progress state (blue)
  // because both are "active waiting" from the user's perspective.
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid="status-queued" title="Queued due to heavy demand. We'll automatically retry shortly.">
        <Clock className="w-3 h-3" />Queued
      </span>
    );
  }
  if (status === "complete") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid="status-complete">
      Complete
    </span>
  );
  return null;
};

// Tiered queued copy. After 2 auto-retries the system stops auto-retrying
// per product spec — surface this clearly so the user knows a manual rerun
// is required to make further progress.
const queuedMessageForRetries = (n) => {
  const r = n || 0;
  if (r >= 2) return "Auto-retries exhausted (2/2) — manual rerun required.";
  return "Queued due to heavy demand. We'll retry automatically.";
};

export default function HistoryPage() {
  const { user } = useAuth();
  const [presentations, setPresentations] = useState([]);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const pollRef = useRef(null);
  
  // Pagination state
  const [presTotal, setPresTotal] = useState(0);
  const [deckTotal, setDeckTotal] = useState(0);
  const [presHasMore, setPresHasMore] = useState(false);
  const [deckHasMore, setDeckHasMore] = useState(false);
  const PAGE_SIZE = 12;
  
  // Feedback state
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Share state
  const [shareTarget, setShareTarget] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  // Public link share (separate from internal org share)
  const [publicShareTarget, setPublicShareTarget] = useState(null);
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);

  // Per-item ETA cache: { [itemId]: { estimate_seconds, kind, approach } }.
  // Backed by /api/generation-eta — fetched lazily for items that are still
  // generating, then reused across polls so we don't re-hit the endpoint on
  // every 4-second tick.
  const [genEtas, setGenEtas] = useState({});

  const fetchAll = async (loadMore = false) => {
    try {
      const presSkip = loadMore ? presentations.length : 0;
      const deckSkip = loadMore ? decks.length : 0;
      
      const [presRes, deckRes] = await Promise.all([
        axios.get(`${API}/my-presentations?limit=${PAGE_SIZE}&skip=${presSkip}`, { withCredentials: true }),
        axios.get(`${API}/my-decks?limit=${PAGE_SIZE}&skip=${deckSkip}`, { withCredentials: true }),
      ]);
      
      if (loadMore) {
        setPresentations(prev => [...prev, ...presRes.data.items]);
        setDecks(prev => [...prev, ...deckRes.data.items]);
      } else {
        setPresentations(presRes.data.items);
        setDecks(deckRes.data.items);
      }
      
      setPresTotal(presRes.data.total);
      setDeckTotal(deckRes.data.total);
      setPresHasMore(presRes.data.has_more);
      setDeckHasMore(deckRes.data.has_more);
      
      return { pres: loadMore ? [...presentations, ...presRes.data.items] : presRes.data.items, decks: loadMore ? [...decks, ...deckRes.data.items] : deckRes.data.items };
    } catch { return { pres: [], decks: [] }; }
    finally { setLoading(false); setLoadingMore(false); }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchAll(true);
  };

  // Lazily fetch the static ETA for any generating items we haven't sized
  // yet. The backend bases this on the user's median for similar past jobs,
  // falling back to a heuristic. We cache by item id so polling doesn't
  // refetch each tick.
  const fetchGenEtas = async (items) => {
    const need = items.filter(i => i.status === "generating" && !genEtas[i.id]);
    if (!need.length) return;
    const results = await Promise.allSettled(need.map(item => {
      const isDeck = item._type === "deck";
      // Research mode is identified by the explicit `approach` field
      // (preferred) OR by the presence of cited sources on the input
      // record. Public/Premium tier is read off `research_model` so
      // the ETA add-on (+15min Public, +30min Premium) matches what
      // the worker is actually doing.
      const approach = (item.approach === "research")
        || ((item.inputs?.sources?.length || 0) > 0)
        ? "research"
        : "design";
      const research_model = item.research_model || "public";
      const num_slides = isDeck ? (item.num_slides || item.slide_ids?.length || 1) : 1;
      const params = { kind: isDeck ? "deck" : "single", approach, num_slides, research_model };
      return axios.get(`${API}/generation-eta`, { params, withCredentials: true })
        .then(res => ({ id: item.id, data: res.data }));
    }));
    setGenEtas(prev => {
      const next = { ...prev };
      for (const r of results) {
        if (r.status === "fulfilled" && r.value?.data?.estimate_seconds) {
          next[r.value.id] = r.value.data;
        }
      }
      return next;
    });
  };

  useEffect(() => {
    fetchAll().then(({ pres, decks: d }) => {
      const allItems = [...pres.map(p => ({ ...p, _type: "slide" })), ...d.map(dk => ({ ...dk, _type: "deck" }))];
      fetchGenEtas(allItems);
      if (pres.some(p => p.status === "generating") || d.some(dk => dk.status === "generating")) {
        pollRef.current = setInterval(async () => {
          const fresh = await fetchAll();
          const freshAll = [...fresh.pres.map(p => ({ ...p, _type: "slide" })), ...fresh.decks.map(dk => ({ ...dk, _type: "deck" }))];
          fetchGenEtas(freshAll);
          if (!fresh.pres.some(p => p.status === "generating") && !fresh.decks.some(dk => dk.status === "generating")) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 4000);
      }
    });

    // Listen for `history:refresh` events fired by the generator after a job
    // is queued. This avoids waiting for the next 4-second poll tick to show
    // a freshly-queued deck (immediate-feedback UX).
    const onRefresh = async () => {
      const fresh = await fetchAll();
      const freshAll = [...fresh.pres.map(p => ({ ...p, _type: "slide" })), ...fresh.decks.map(dk => ({ ...dk, _type: "deck" }))];
      fetchGenEtas(freshAll);
      if (!pollRef.current && (fresh.pres.some(p => p.status === "generating") || fresh.decks.some(dk => dk.status === "generating"))) {
        pollRef.current = setInterval(async () => {
          const next = await fetchAll();
          if (!next.pres.some(p => p.status === "generating") && !next.decks.some(dk => dk.status === "generating")) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 4000);
      }
    };
    window.addEventListener("history:refresh", onRefresh);

    return () => {
      window.removeEventListener("history:refresh", onRefresh);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = [
    ...presentations.filter(p => !p.deck_id).map(p => ({ ...p, _type: "slide" })),
    ...decks.map(d => ({ ...d, _type: "deck" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filtered = activeFilter === "all" ? allItems
    : activeFilter === "slides" ? allItems.filter(i => i._type === "slide")
    : activeFilter === "decks" ? allItems.filter(i => i._type === "deck")
    : allItems.filter(i => i.is_shared);

  const handleDownload = (item) => {
    // Don't override `a.download` here. The server sends a slugified
    // `<title>.pptx` filename via Content-Disposition; browsers respect
    // that header when `a.download` is empty, so we get a properly-named
    // file end-to-end. (Setting `a.download = "deck.pptx"` here used to
    // clobber every download to a generic name.)
    const a = document.createElement("a");
    a.href = item._type === "deck" ? `${API}/download-deck/${item.id}` : `${API}/download/${item.id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const endpoint = deleteTarget._type === "deck" ? `${API}/decks/${deleteTarget.id}` : `${API}/presentations/${deleteTarget.id}`;
      await axios.delete(endpoint);
      if (deleteTarget._type === "deck") setDecks(prev => prev.filter(d => d.id !== deleteTarget.id));
      else setPresentations(prev => prev.filter(p => p.id !== deleteTarget.id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  const handleRetry = async (item) => {
    try {
      const endpoint = item._type === "deck" ? `${API}/decks/${item.id}/retry` : `${API}/presentations/${item.id}/retry`;
      await axios.post(endpoint);
      toast.success("Retrying...");
      fetchAll();
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          const fresh = await fetchAll();
          if (!fresh.pres.some(p => p.status === "generating") && !fresh.decks.some(dk => dk.status === "generating")) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 4000);
      }
    } catch { toast.error("Retry failed"); }
  };

  const handleFeedback = async (item, rating) => {
    try {
      // Toggle: clicking the active thumb clears it.
      const next = item.feedback?.rating === rating ? null : rating;
      const endpoint = item._type === "deck" 
        ? `${API}/decks/${item.id}/feedback` 
        : `${API}/presentations/${item.id}/feedback`;
      await axios.post(endpoint, { rating: next }, { withCredentials: true });
      toast.success(next ? "Thanks for your feedback!" : "Feedback cleared");
      if (item._type === "deck") {
        setDecks(prev => prev.map(d => d.id === item.id ? { ...d, feedback: { ...d.feedback, rating: next } } : d));
      } else {
        setPresentations(prev => prev.map(p => p.id === item.id ? { ...p, feedback: { ...p.feedback, rating: next } } : p));
      }
    } catch { toast.error("Failed to submit feedback"); }
  };

  const handleSubmitFeedbackText = async () => {
    if (!feedbackTarget) return;
    setSubmittingFeedback(true);
    try {
      const endpoint = feedbackTarget._type === "deck" 
        ? `${API}/decks/${feedbackTarget.id}/feedback` 
        : `${API}/presentations/${feedbackTarget.id}/feedback`;
      await axios.post(endpoint, { rating: feedbackTarget.feedback?.rating, feedback_text: feedbackText }, { withCredentials: true });
      toast.success("Feedback submitted!");
      if (feedbackTarget._type === "deck") {
        setDecks(prev => prev.map(d => d.id === feedbackTarget.id ? { ...d, feedback: { ...d.feedback, feedback_text: feedbackText } } : d));
      } else {
        setPresentations(prev => prev.map(p => p.id === feedbackTarget.id ? { ...p, feedback: { ...p.feedback, feedback_text: feedbackText } } : p));
      }
      setFeedbackTarget(null);
      setFeedbackText("");
    } catch { toast.error("Failed to submit feedback"); }
    finally { setSubmittingFeedback(false); }
  };

  // Share functions
  const openShareDialog = async (item) => {
    setShareTarget(item);
    setShareEmail("");
    setOrgUsersLoading(true);
    try {
      const res = await axios.get(`${API}/users/in-org`, { withCredentials: true });
      setOrgUsers(res.data);
    } catch { setOrgUsers([]); }
    finally { setOrgUsersLoading(false); }
  };

  const handleShare = async (email) => {
    if (!shareTarget) return;
    setSharing(true);
    try {
      const endpoint = shareTarget._type === "deck"
        ? `${API}/decks/${shareTarget.id}/share`
        : `${API}/presentations/${shareTarget.id}/share`;
      const res = await axios.post(endpoint, { recipient_email: email }, { withCredentials: true });
      toast.success(res.data.message);
      setShareTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to share");
    } finally { setSharing(false); }
  };

  const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy h:mm a"); } catch { return d; } };
  const slideCount = presentations.filter(p => !p.deck_id).length;
  const sharedCount = allItems.filter(i => i.is_shared).length;

  return (
    <div className="page-container" data-testid="history-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Home</span><span>/</span><span className="text-gray-900">History</span>
      </div>
      <h1 className="text-2xl font-bold font-['Manrope'] mb-6">My Generations</h1>

      {/* Celebratory referral banner — surfaces unread `referral_signup`
          notifications (fired when a friend signs up via the user's
          referral link). Self-dismissing once acknowledged. */}
      <ReferralCelebrationBanner />

      {allItems.length > 0 && (
        <div className="flex gap-1 mb-5 flex-wrap">
          {[
            { key: "all", label: "All", count: allItems.length }, 
            { key: "slides", label: "Slides", count: slideCount }, 
            { key: "decks", label: "Decks", count: decks.length },
            ...(sharedCount > 0 ? [{ key: "shared", label: "Shared with me", count: sharedCount }] : []),
          ].map(f => (
            <button key={f.key} onClick={() => setActiveFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${activeFilter === f.key ? "bg-black text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
              data-testid={`filter-${f.key}`}>{f.label} ({f.count})</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-200 bg-gray-50/50 max-w-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Presentation className="w-10 h-10 text-gray-300 mb-3" />
            <h3 className="text-sm font-medium text-gray-700 mb-1">No generations yet</h3>
            <p className="text-xs text-gray-400 mb-4">Get started by creating your first presentation</p>
            <Link to="/generator">
              <Button className="bg-black hover:bg-gray-800 text-white" data-testid="create-presentation-cta">
                <Plus className="w-4 h-4 mr-1.5" />Create a presentation
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(item => {
            const status = item.status || "complete";
            const isGen = status === "generating";
            // Failed jobs always render as "Queued" with a Retry button —
            // we never expose a terminal "Failure" state in the UI any more.
            const isQueued = status === "failed";
            const isDeck = item._type === "deck";
            const slides = isDeck ? (item.num_slides || item.slide_ids?.length || 0) : null;
            // Static ETA + "still within estimate?" decision. Once elapsed
            // exceeds the estimate we hide the ~Xm line and let the
            // existing spinner carry the UI per product spec.
            const eta = genEtas[item.id];
            let showEta = false;
            if (isGen && eta?.estimate_seconds && item.created_at) {
              try {
                const startMs = new Date(item.created_at).getTime();
                const elapsedSec = (Date.now() - startMs) / 1000;
                showEta = elapsedSec < eta.estimate_seconds;
              } catch { showEta = true; }
            }
            const formatEtaShort = (s) => {
              if (!s || s <= 0) return "1m";
              if (s < 90) return "~1m";
              return `~${Math.round(s / 60)}m`;
            };

            return (
              <Card key={`${item._type}-${item.id}`}
                className={`border-gray-200 shadow-none hover:shadow-md transition-shadow overflow-hidden flex flex-col ${isGen ? "border-blue-200" : ""}`}
                data-testid={`history-card-${item.id}`}>
                {/* Shared indicator */}
                {item.is_shared && (
                  <div className="bg-blue-50 border-b border-blue-200 px-3 py-1.5 flex items-center gap-1.5">
                    <Users className="w-3 h-3 text-blue-500" />
                    <span className="text-[10px] font-medium text-blue-600">Shared by {item.shared_by_name}</span>
                  </div>
                )}
                {/* Thumbnail (clickable when complete) */}
                {(() => {
                  const thumbInner = (
                    <div className={`aspect-video relative flex items-center justify-center ${isGen ? "bg-gradient-to-br from-blue-50 to-blue-100" : isQueued ? "bg-gradient-to-br from-blue-50 to-blue-100" : "bg-gradient-to-br from-gray-100 to-gray-200"}`}>
                  {isGen ? (
                    <div className="flex flex-col items-center text-blue-500" data-testid={`gen-card-${item.id}`}>
                      <Loader2 className="w-8 h-8 animate-spin mb-1" />
                      <span className="text-xs">Generating...</span>
                      {showEta && (
                        <span className="text-[10px] mt-0.5 flex items-center gap-1 text-blue-700" data-testid={`gen-eta-${item.id}`}>
                          <Clock className="w-2.5 h-2.5" />Usually takes {formatEtaShort(eta.estimate_seconds)}
                        </span>
                      )}
                    </div>
                  ) : isQueued ? (
                    <div className="flex flex-col items-center text-blue-600 text-center px-3">
                      <Clock className="w-8 h-8 mb-1" />
                      <span className="text-xs font-medium">Queued</span>
                      <span className="text-[10px] mt-0.5 text-blue-700 leading-snug">due to heavy demand</span>
                    </div>
                  ) : item.status === "complete" ? (
                    <>
                      <img src={`${API}/thumbnails/${isDeck && item.slide_ids?.length > 0 ? item.slide_ids[0] : item.id}?v=${item._version || 0}`} alt="Preview" className="w-full h-full object-cover" loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                      />
                      <div className="flex-col items-center text-gray-400" style={{display:'none'}}>
                        {isDeck ? <Layers className="w-10 h-10 mb-1" /> : <Presentation className="w-10 h-10 mb-1" />}
                        <span className="text-xs">{isDeck ? `${slides} slides` : "PPTX"}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-gray-400">
                      {isDeck ? <Layers className="w-10 h-10 mb-1" /> : <Presentation className="w-10 h-10 mb-1" />}
                      <span className="text-xs">{isDeck ? `${slides} slides` : "PPTX"}</span>
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 ${isDeck ? "text-indigo-700 bg-indigo-50 border border-indigo-200" : "text-gray-500 bg-white/80 border border-gray-200"}`}>
                      {isDeck ? <><Layers className="w-2.5 h-2.5" />Deck</> : <><FileText className="w-2.5 h-2.5" />Slide</>}
                    </span>
                  </div>
                  {item.template_name && (
                    <div className="absolute top-2 right-2">
                      <span className="text-[10px] font-medium text-gray-600 bg-white/80 rounded-full px-2 py-0.5 border border-gray-200">{item.template_name}</span>
                    </div>
                  )}
                </div>
                  );
                  return status === "complete" ? (
                    <Link to={`/details/${isDeck ? "deck" : "slide"}/${item.id}`} className="block hover:opacity-95 transition-opacity" data-testid={`thumb-link-${item.id}`}>
                      {thumbInner}
                    </Link>
                  ) : thumbInner;
                })()}

                <CardContent className="p-4 flex flex-col flex-1">
                  <p className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1.5 min-h-[2.6em]" data-testid={`item-title-${item.id}`} title={isDeck ? (item.topic || item.inputs?.topic || "Untitled Deck") : (item.inputs?.title || item.topic || "Untitled Slide")}>
                    {isDeck
                      ? (item.topic || item.inputs?.topic || "Untitled Deck")
                      : (item.inputs?.title || item.topic || "Untitled Slide")}
                  </p>
                  {(isGen || isQueued) && <div className="mb-1.5"><StatusBadge status={status} detail={item.status_detail} /></div>}
                  {isQueued && (
                    <p className="text-[10px] text-gray-500 mb-1.5 leading-snug" data-testid={`queued-msg-${item.id}`}>{queuedMessageForRetries(item.retry_count)}</p>
                  )}
                  {isQueued && item.credits_refunded && (
                    <p className="text-[10px] mb-1.5 inline-flex items-center gap-1 self-start text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-medium" title="The credits charged for this attempt have been refunded to your balance" data-testid={`refunded-chip-${item.id}`}>
                      <RotateCcw className="w-2.5 h-2.5" />Credits refunded
                    </p>
                  )}
                  <div className="flex items-center gap-1 text-xs text-gray-400 mb-3 flex-wrap">
                    <Calendar className="w-3 h-3" /><span>{fmtDate(item.created_at)}</span>
                    {isDeck && slides && <span className="text-gray-300">&middot; {slides} slides</span>}
                    {/* Sources chip removed (2026-05-11) — cluttered the card and
                        duplicated info that's already on the details page. */}
                  </div>

                  {/* Spacer pushes Rate + Actions to the bottom for consistent vertical alignment */}
                  <div className="flex-1" />

                  {/* Feedback Row - Only show for completed items */}
                  {status === "complete" && (
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                      <span className="text-xs text-gray-400">Rate:</span>
                      <button onClick={() => handleFeedback(item, "up")}
                        className={`p-1.5 rounded transition-colors ${item.feedback?.rating === "up" ? "bg-green-100 text-green-600" : "hover:bg-gray-100 text-gray-400"}`}
                        data-testid={`like-${item.id}`}>
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleFeedback(item, "down")}
                        className={`p-1.5 rounded transition-colors ${item.feedback?.rating === "down" ? "bg-red-100 text-red-600" : "hover:bg-gray-100 text-gray-400"}`}
                        data-testid={`dislike-${item.id}`}>
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setFeedbackTarget(item); setFeedbackText(item.feedback?.feedback_text || ""); }}
                        className={`p-1.5 rounded transition-colors ${item.feedback?.feedback_text ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100 text-gray-400"}`}
                        title="Add feedback" data-testid={`feedback-${item.id}`}>
                        <MessageSquare className="w-3.5 h-3.5" />
                      </button>
                      {item.feedback?.feedback_text && (
                        <span className="text-[10px] text-gray-400 truncate max-w-[100px]" title={item.feedback.feedback_text}>
                          "{item.feedback.feedback_text.slice(0, 30)}..."
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      {isGen ? (
                        <span className="text-xs text-gray-400">In progress...</span>
                      ) : isQueued ? (
                        <button onClick={() => handleRetry(item)} className="flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-800" data-testid={`retry-${item.id}`}>
                          <RefreshCw className="w-3.5 h-3.5" />Re-run
                        </button>
                      ) : (
                        <>
                          <Link to={`/details/${isDeck ? "deck" : "slide"}/${item.id}`}
                            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-black transition-colors" data-testid={`view-${item.id}`}>
                            <Eye className="w-3.5 h-3.5" />Details
                          </Link>
                          <button onClick={() => handleDownload(item)}
                            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-black transition-colors" data-testid={`download-${item.id}`}>
                            <Download className="w-3.5 h-3.5" />Download
                          </button>
                          {!item.is_shared && (
                            <button onClick={() => setPublicShareTarget(item)}
                              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-teal-600 transition-colors"
                              data-testid={`share-link-${item.id}`}>
                              <Share2 className="w-3.5 h-3.5" />Share
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {!item.is_shared && (
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(item)}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0" data-testid={`delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Load More Button */}
        {(presHasMore || deckHasMore) && (
          <div className="flex justify-center mt-6">
            <Button 
              variant="outline" 
              onClick={handleLoadMore} 
              disabled={loadingMore}
              className="min-w-[200px]"
              data-testid="load-more-btn"
            >
              {loadingMore ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading...</>
              ) : (
                <>Load More ({presTotal + deckTotal - presentations.length - decks.length} remaining)</>
              )}
            </Button>
          </div>
        )}
      </>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?._type === "deck" ? "deck" : "slide"}?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={!!feedbackTarget} onOpenChange={() => setFeedbackTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Feedback</DialogTitle>
            <DialogDescription>Share your thoughts about this {feedbackTarget?._type === "deck" ? "deck" : "slide"}.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea placeholder="What could be improved? Any specific issues or suggestions?" value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)} className="min-h-[100px]" data-testid="feedback-text-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackTarget(null)}>Cancel</Button>
            <Button onClick={handleSubmitFeedbackText} disabled={submittingFeedback} className="bg-black hover:bg-gray-800">
              {submittingFeedback ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!shareTarget} onOpenChange={() => setShareTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5" />Share {shareTarget?._type === "deck" ? "Deck" : "Slide"}</DialogTitle>
            <DialogDescription>Share with a team member in your organization</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Quick share with org members */}
            {orgUsersLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : orgUsers.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Team Members</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {orgUsers.map(u => (
                    <button key={u.user_id} onClick={() => handleShare(u.email)}
                      disabled={sharing}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      data-testid={`share-user-${u.user_id}`}>
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                        <p className="text-xs text-gray-400 truncate">{u.email}</p>
                      </div>
                      <Share2 className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-2">No team members found in your organization</p>
            )}
            {/* Manual email */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Or share by email</p>
              <div className="flex gap-2">
                <Input placeholder="colleague@company.com" value={shareEmail}
                  onChange={e => setShareEmail(e.target.value)} className="flex-1" data-testid="share-email-input" />
                <Button onClick={() => handleShare(shareEmail)} disabled={!shareEmail || sharing} className="bg-black hover:bg-gray-800">
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Share"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Public Share Link Dialog */}
      <ShareLinkDialog
        open={!!publicShareTarget}
        onClose={() => setPublicShareTarget(null)}
        item={publicShareTarget}
      />
    </div>
  );
}
